import { Subject } from 'rxjs';
import * as _ from 'underscore';
import { Renderer2, Injectable } from '@angular/core';

export interface ICodeInput {
	name: string;
	key: string;
	value: string;
}
@Injectable()
export class ICodeService {
	private _codeSubj = new Subject<string>();
	private _codeSubmittedSubj = new Subject<void>();
	private _requestCodeSubj = new Subject<void>();
	private _codeSentSubj = new Subject<number>();

	public code$ = this._codeSubj.asObservable();
	public codeRequested$ = this._requestCodeSubj.asObservable();
	public codeSubmitted$ = this._codeSubmittedSubj.asObservable();
	public newCodeSent$ = this._codeSentSubj.asObservable();

	private readonly _isIOS: boolean;
	private readonly _isAndroid: boolean;

	constructor() {
		this._isIOS = navigator.userAgent.match(/iPhone|iPad|iPod/i) !== undefined;
		this._isAndroid = navigator.userAgent.match(/Android/i) !== undefined;
	}

	public config: ICodeInput[] = [
		{
			name: 'digit-1',
			key: 'digit-1',
			value: '',
		},
		{
			name: 'digit-2',
			key: 'digit-2',
			value: '',
		},
		{
			name: 'digit-3',
			key: 'digit-3',
			value: '',
		},
		{
			name: 'digit-4',
			key: 'digit-4',
			value: '',
		},
		{
			name: 'digit-5',
			key: 'digit-5',
			value: '',
		},
		{
			name: 'digit-6',
			key: 'digit-6',
			value: '',
		},
	];

	public updateCode(icode: string) {
		this._codeSubj.next(icode);
	}

	public requestCode(): void {
		this._requestCodeSubj.next();
	}

	public resetTimer(codeLifetime = 300): void {
		this._codeSentSubj.next(codeLifetime);
	}

	public codeSubmitted(): void {
		this._codeSubmittedSubj.next();
	}

	public clearCode() {
		this.config.forEach((item) => (item.value = ''));
		this.updateCode(null);
	}

	public isValidCode(code: string): boolean {
		return code && (code.length == 8 || code.length == 6);
	}

	public moveCursor(event: KeyboardEvent, prefix: string, index: number, renderer2: Renderer2) {
		let key = event.charCode || event.keyCode || 0;

		if (this._isAndroid && key === 229) {
			setTimeout(() => {
				const target = <HTMLInputElement>event.target;
				// android devices fix
				key = target.value.charCodeAt(target.value.length - 1);
				this.handleKey(key, prefix, index, renderer2);
			}, 0);
		} else if (this._isIOS) {
			setTimeout(() => {
				this.handleKey(key, prefix, index, renderer2);
			}, 0);
		} else {
			this.handleKey(key, prefix, index, renderer2);
		}
	}

	private handleKey(key: number, prefix: string, index: number, renderer2: Renderer2): void {
		let code: string = '';
		// allow tab, enter, arrows, numbers and keypad numbers ONLY
		// home, end, period, and numpad decimal
		if (
			key === 9 ||
			key === 13 ||
			key === 46 ||
			key === 110 ||
			key === 190 ||
			(key >= 35 && key <= 40)
		) {
			return;
		} else if (key === 8) {
			// backspace
			if (index + 1 > 1) {
				if (this.config[index].value !== '') {
					this.config[index].value = '';
					this.config.forEach((item) => (code += item.value));
					this.updateCode(code);
				} else if (this.config[index].value === '') {
					this.config[index - 1].value = '';
					this.config.forEach((item) => (code += item.value));
					this.updateCode(code);
					setTimeout(() => {
						let prevEl = renderer2.selectRootElement('#' + prefix + index.toString());
						prevEl.focus();
					}, 25);
				}
			}
		} else if ((key >= 48 && key <= 57) || (key >= 96 && key <= 105)) {
			this.config[index].value = this.setKeyChar(key);
			if (index + 1 < this.config.length) {
				setTimeout(() => {
					let nextEl = renderer2.selectRootElement('#' + prefix + (index + 2).toString());
					nextEl.focus();
				}, 25);
			}
			this.config.forEach((item) => (code += item.value));
			this.updateCode(code);
		} else return;
	}

	private setKeyChar(key: number): string {
		if (key >= 96 && key <= 105) {
			key -= 48;
		}
		return String.fromCharCode(key);
	}
}
